/**
 * Steps de cada tour por página.
 *
 * Cada step usa `target` (string CSS) - preferimos `[data-tour="..."]`
 * para não acoplar ao DOM real.
 *
 * Para adicionar um tour a uma página nova:
 *   1. Marque elementos com `data-tour="meu-id"` no JSX
 *   2. Adicione a entrada em `tourSteps[novaChave]`
 *   3. (Opcional) Inclua a chave em TOUR_ORDER para que o tour completo
 *      passe por ela.
 */
// Tipo de Step relaxado - usamos `any` para evitar incompatibilidades entre
// versões do react-joyride. Os campos efetivamente usados são `target`,
// `content`, `title`, `placement`, `disableBeacon`, `data`.
type Step = any;

export type TourKey =
  | "dashboard"
  | "invoiceList"
  | "invoiceCreate"
  | "customers"
  | "payers"
  | "tags"
  | "companies"
  | "users"
  | "preferences";

// Step com `data.route` indica para onde navegar quando o tour completo
// passar para a próxima página.
type TourStep = Step & { data?: { route?: string } };

export const tourSteps: Record<TourKey, TourStep[]> = {
  dashboard: [
    {
      target: 'body',
      placement: "center",
      title: "Bem-vindo ao Split Fácil!",
      content:
        "Vamos fazer um tour rápido pelo painel. Você pode pular a qualquer momento - basta clicar em \"Pular tour\".",
      disableBeacon: true,
      data: { route: "/" },
    },
    {
      target: '[data-tour="dashboard-hero"]',
      title: "Painel inicial",
      content:
        "Aqui você vê um resumo financeiro: total de cobranças, recebidas, aguardando e taxa de pagamento dos seus clientes.",
      placement: "bottom",
    },
    {
      target: '[data-tour="dashboard-quick-actions"]',
      title: "Ações rápidas",
      content:
        "Crie uma nova cobrança em segundos ou cadastre um novo recebedor diretamente do painel.",
      placement: "top",
    },
    {
      target: '[data-tour="dashboard-recent"]',
      title: "Cobranças recentes",
      content:
        "Suas últimas cobranças aparecem aqui com status atualizado em tempo real conforme os pagamentos chegam.",
      placement: "top",
    },
  ],

  invoiceCreate: [
    {
      target: 'body',
      placement: "center",
      title: "Criando uma nova cobrança",
      content:
        "Esta é a tela mais importante: aqui você gera cobranças e configura como o dinheiro será dividido.",
      disableBeacon: true,
      data: { route: "/invoices/create" },
    },
    {
      target: '[data-tour="invoice-payer"]',
      title: "Quem vai pagar",
      content:
        "Escolha um pagador já cadastrado ou crie um novo. Os dados pessoais ficam salvos para próximas cobranças.",
      placement: "right",
    },
    {
      target: '[data-tour="invoice-receivers"]',
      title: "Quem vai receber",
      content:
        "Adicione um ou vários recebedores. Cada um pode ter um percentual ou valor fixo. O Split Fácil distribui automaticamente quando o pagamento cair.",
      placement: "right",
    },
    {
      target: '[data-tour="invoice-payment-method"]',
      title: "Forma de pagamento",
      content:
        "PIX para receber na hora, boleto para vencimento futuro. Cada forma tem sua taxa do gateway Pagar.me.",
      placement: "right",
    },
  ],

  invoiceList: [
    {
      target: 'body',
      placement: "center",
      title: "Cobranças",
      content:
        "Aqui ficam todas as cobranças criadas - pagas, aguardando e canceladas.",
      disableBeacon: true,
      data: { route: "/invoices" },
    },
    {
      target: '[data-tour="page-hero"]',
      title: "Resumo no topo",
      content:
        "Veja no topo o total da página, quantas foram pagas e quantas estão aguardando pagamento.",
      placement: "bottom",
    },
    {
      target: '[data-tour="filters"]',
      title: "Filtros",
      content:
        "Filtre por status (pago, aguardando, cancelado), por pagador específico ou por período.",
      placement: "bottom",
    },
    {
      target: '[data-tour="table"]',
      title: "Lista de cobranças",
      content:
        "Clique no ícone do olho de cada linha para ver detalhes, QR Code do PIX, link de pagamento ou comprovante.",
      placement: "top",
    },
  ],

  customers: [
    {
      target: 'body',
      placement: "center",
      title: "Recebedores",
      content:
        "Cadastre as pessoas e empresas que recebem dinheiro dos seus splits. Os dados bancários são salvos com segurança.",
      disableBeacon: true,
      data: { route: "/customers/receivers" },
    },
    {
      target: '[data-tour="page-hero"]',
      title: "Sua carteira de recebedores",
      content:
        "Aqui você vê todos os recebedores ativos. Quanto mais cadastrados, mais ágil será criar cobranças com split.",
      placement: "bottom",
    },
    {
      target: '[data-tour="create-button"]',
      title: "Adicionar novo",
      content:
        "Cadastre um novo recebedor com nome, CPF/CNPJ, e-mail e dados bancários. Ele já fica disponível para usar nos splits.",
      placement: "left",
    },
  ],

  payers: [
    {
      target: 'body',
      placement: "center",
      title: "Pagadores",
      content:
        "Aqui ficam as pessoas que pagam suas cobranças. Mantenha cadastrado para gerar novas cobranças mais rápido.",
      disableBeacon: true,
      data: { route: "/customers/payers" },
    },
    {
      target: '[data-tour="page-hero"]',
      title: "Quem paga suas cobranças",
      content:
        "Pagadores são contatos que você cobra recorrentemente. Não precisam de dados bancários.",
      placement: "bottom",
    },
  ],

  tags: [
    {
      target: 'body',
      placement: "center",
      title: "Grupos de recebedores",
      content:
        "Grupos são atalhos para várias pessoas que sempre recebem juntas - uma banda, uma equipe, sócios de um projeto.",
      disableBeacon: true,
      data: { route: "/tags" },
    },
    {
      target: '[data-tour="page-hero"]',
      title: "Crie grupos para agilizar",
      content:
        "Em vez de adicionar 5 recebedores manualmente em cada cobrança, crie um grupo e use ele direto no split.",
      placement: "bottom",
    },
  ],

  companies: [
    {
      target: 'body',
      placement: "center",
      title: "Minhas contas bancárias",
      content:
        "Aqui você cadastra a conta bancária da sua empresa, onde a sua parte do split (após pagamento dos outros) será depositada.",
      disableBeacon: true,
      data: { route: "/companies" },
    },
    {
      target: '[data-tour="page-hero"]',
      title: "Sua conta de recebimento",
      content:
        "Você pode ter mais de uma conta cadastrada. Configure pelo menos uma para começar a receber.",
      placement: "bottom",
    },
  ],

  users: [
    {
      target: 'body',
      placement: "center",
      title: "Funcionários",
      content:
        "Convide pessoas da sua equipe para usarem o Split Fácil com diferentes níveis de permissão.",
      disableBeacon: true,
      data: { route: "/users" },
    },
    {
      target: '[data-tour="page-hero"]',
      title: "Sua equipe",
      content:
        "Cada funcionário tem um perfil: Administrador, Gerente ou Visualizador. Você decide o que cada um pode fazer.",
      placement: "bottom",
    },
  ],

  preferences: [
    {
      target: 'body',
      placement: "center",
      title: "Preferências",
      content:
        "Aqui você ajusta seus dados pessoais, troca a senha e pode reiniciar este tour quando quiser.",
      disableBeacon: true,
      data: { route: "/preferences" },
    },
    {
      target: '[data-tour="preferences-profile"]',
      title: "Seu perfil",
      content:
        "Atualize nome, telefone e e-mail de contato. Seu e-mail também é o login da conta.",
      placement: "right",
    },
    {
      target: '[data-tour="preferences-tour"]',
      title: "Reiniciar tour",
      content:
        "Quando quiser rever este tour, clique aqui. É útil quando uma nova pessoa da sua equipe entra.",
      placement: "left",
    },
  ],
};

// Ordem do tour completo no primeiro acesso
export const TOUR_ORDER: TourKey[] = [
  "dashboard",
  "invoiceCreate",
  "invoiceList",
  "customers",
  "payers",
  "tags",
  "companies",
  "users",
  "preferences",
];

// Mapeia rotas a chaves de tour
export const getTourKeyForPath = (pathname: string): TourKey | null => {
  if (pathname === "/" || pathname.startsWith("/dashboard")) return "dashboard";
  if (pathname.startsWith("/invoices/create")) return "invoiceCreate";
  if (pathname.startsWith("/invoices")) return "invoiceList";
  if (pathname.startsWith("/customers/payers")) return "payers";
  if (pathname.startsWith("/customers")) return "customers";
  if (pathname.startsWith("/tags")) return "tags";
  if (pathname.startsWith("/companies")) return "companies";
  if (pathname.startsWith("/users")) return "users";
  if (pathname.startsWith("/preferences")) return "preferences";
  return null;
};
